import { useEffect } from "react";
import { Activity } from "../models/Activity";

const parsePixelWidth = (value: string | null | undefined) => {
  if (!value) return undefined;

  const normalizedValue = value.trim();

  if (!normalizedValue || normalizedValue.includes("%")) return undefined;

  const matchedValue = normalizedValue.match(/^(\d+(?:\.\d+)?)px$/i);

  if (matchedValue) {
    return Number.parseFloat(matchedValue[1]);
  }

  const numericValue = Number.parseFloat(normalizedValue);
  return Number.isFinite(numericValue) ? numericValue : undefined;
};

const getColWidthAttribute = (element: HTMLElement) => {
  const colWidthValue = element.getAttribute("colwidth");

  if (!colWidthValue || colWidthValue.includes(",")) return undefined;

  return parsePixelWidth(colWidthValue);
};

const getElementWidthBudget = (element: HTMLElement) => {
  return (
    parsePixelWidth(element.style.width) ??
    parsePixelWidth(element.getAttribute("width")) ??
    getColWidthAttribute(element) ??
    0
  );
};

const clearExplicitWidths = (element: HTMLElement) => {
  if (parsePixelWidth(element.style.width) !== undefined) {
    element.style.removeProperty("width");
  }

  if (parsePixelWidth(element.style.minWidth) !== undefined) {
    element.style.removeProperty("min-width");
  }

  if (parsePixelWidth(element.getAttribute("width")) !== undefined) {
    element.removeAttribute("width");
  }
};

/**
 * Hook to ensure all tables with data-table-width attribute have their CSS width applied
 * This ensures backward compatibility and proper rendering of activity content
 */
export const useApplyTableWidths = (
  containerRef: React.RefObject<HTMLElement>,
  activity: Activity | undefined
) => {
  useEffect(() => {
    const container = containerRef.current;

    if (!container) return;

    const applyTableWidths = () => {
      const tables = container.querySelectorAll<HTMLTableElement>(
        "table[data-table-width]"
      );

      tables.forEach((table) => {
        const width = table.getAttribute("data-table-width");

        if (width && width !== table.style.width) {
          table.style.width = `${width} !important`;
          table.style.setProperty("width", width, "important");
          table.style.setProperty("min-width", width, "important");
        }

        const availableWidth = container.clientWidth;

        if (!availableWidth) {
          return;
        }

        const columnWidthBudget = Array.from(
          table.querySelectorAll<HTMLElement>("col")
        ).reduce(
          (totalWidth, element) => totalWidth + getElementWidthBudget(element),
          0
        );

        const rowWidthBudget = Array.from(table.rows).reduce(
          (largestRowWidth, row) => {
            const currentRowWidth = Array.from(row.cells).reduce(
              (totalWidth, cell) => totalWidth + getElementWidthBudget(cell),
              0
            );

            return Math.max(largestRowWidth, currentRowWidth);
          },
          0
        );

        const totalExplicitWidth = Math.max(columnWidthBudget, rowWidthBudget);
        const renderedWidth = Math.max(
          table.scrollWidth,
          table.getBoundingClientRect().width
        );

        if (
          totalExplicitWidth <= availableWidth &&
          renderedWidth <= availableWidth
        ) {
          return;
        }

        table
          .querySelectorAll<HTMLElement>("col, th, td")
          .forEach(clearExplicitWidths);
      });
    };

    applyTableWidths();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList" || mutation.type === "attributes") {
          applyTableWidths();
        }
      });
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-table-width", "style"],
    });

    const resizeObserver = new ResizeObserver(() => {
      applyTableWidths();
    });

    resizeObserver.observe(container);

    return () => {
      observer.disconnect();
      resizeObserver.disconnect();
    };
  }, [containerRef, activity]);
};
